/**
 * useEditorCore.ts
 * 
 * Facade hook that composes all editor hooks and business logic.
 * Extracted from App.tsx to enable both the main App and the Pro UI
 * to share the same business logic without duplication.
 * 
 * This does NOT modify App.tsx — that file continues to use
 * its own inline logic. This hook is consumed by ProApp.tsx.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/functions';

import { Subtitle, SubtitleStyle, Clip, Track, EditingSuggestion, MontageVersion, ChatMessage, SavedProject, VideoVersion, MasterTemplate, ChatPendingChoice } from '../types';
import { MontageElements } from '../components/RightMenu/ChatTab';
import { GeneratedVoice } from '../components/RightMenu/constants';
import { Project as SidebarProject, Folder } from '../components/SidebarProjectList';

import { useClipManagement } from './useClipManagement';
import { useProjectState } from './useProjectState';
import { useExportManager } from './useExportManager';
import { useChat } from './useChat';

import { auth } from '../firebase';
import {
    callGemini,
    getR2UploadUrl,
    translateSubtitles,
    translateSubtitlesBatch,
    enhanceAudio,
    toggleEyeContact,
    enhanceVideo,
    checkEnhanceStatus,
    transcribeAudioConstance,
    functions
} from '../services/firebase-api';
import {
    saveProjectToFirestore,
    getProjectsFromFirestore,
    deleteProjectFromFirestore,
    renameProjectInFirestore,
    getProjectById,
    shareProject,
    saveMasterTemplate
} from '../firestore';
import { uploadFileToR2 } from '../services/upload-manager';
import {
    createNewClip,
    getMediaDurationFromUrl,
    generateThumbnails,
    generateWaveformData,
    generateThumbnailFromUrl,
    parseAiJsonResponse
} from '../utils';
import { updateClipDeep, findClipDeep, getClipLocalSourceUrl, getClipSourceUrl, mapClipsDeep, flattenClipsDeep, getRemoteVideoClipsNeedingLocalCache } from '../utils/clip-helpers';
import { fixSubtitleOverlaps } from '../utils/subtitle-helpers';
import {
    buildAutoMontageForkPlan,
    getMontageFingerprint,
    isOriginalMontageVersion
} from '../utils/montage-versioning';
import { generateSRT, formatSRTTime } from '../utils/srt';
import { FFmpegService } from '../ffmpeg-service';
import { getConverter } from '../webm-to-mp4-converter';
import { Type } from "@google/genai";

// Default structures for library items
const DEFAULT_SYNTHE_CLIPS: Clip[] = [
    {
        id: `synthe-text-name-${Date.now()}`,
        name: 'Nom',
        type: 'text',
        trackId: 't1',
        start: 0,
        duration: 5,
        originalDuration: 5,
        trimStart: 0,
        text: 'Nom Prénom',
        style: { fontFamily: 'Futura, sans-serif', fontSize: 42, color: '#FFFFFF', textAlign: 'left' as const, textBaseline: 'middle' as const },
        transform: { x: -300, y: 150, scale: 1, rotation: 0 },
        opacity: 1,
        fadeInDuration: 0.3,
        fadeOutDuration: 0.3,
        volume: 1,
    } as Clip,
    {
        id: `synthe-text-title-${Date.now() + 1}`,
        name: 'Titre',
        type: 'text',
        trackId: 't1',
        start: 0,
        duration: 5,
        originalDuration: 5,
        trimStart: 0,
        text: 'Fonction / Titre',
        style: { fontFamily: 'Futura, sans-serif', fontSize: 28, color: '#CCCCCC', textAlign: 'left' as const, textBaseline: 'middle' as const },
        transform: { x: -300, y: 210, scale: 1, rotation: 0 },
        opacity: 1,
        fadeInDuration: 0.3,
        fadeOutDuration: 0.3,
        volume: 1,
    } as Clip,
];

const DEFAULT_PANEL_CLIPS: Clip[] = [
    {
        id: `panel-bg-${Date.now()}`,
        name: 'Background',
        type: 'image',
        trackId: 'a1',
        start: 0,
        duration: 5,
        originalDuration: 5,
        trimStart: 0,
        url: 'https://medias.storystudio.cc/IntroOutro/BG.png',
        transform: { x: 0, y: 0, scale: 1, rotation: 0 },
        opacity: 1,
        fadeInDuration: 0,
        fadeOutDuration: 0,
        volume: 1,
    } as Clip,
    {
        id: `panel-text-title-${Date.now()}`,
        name: 'Title',
        type: 'text',
        trackId: 't1',
        start: 0,
        duration: 5,
        originalDuration: 5,
        trimStart: 0,
        text: 'VOTRE TEXTE ICI',
        style: { fontFamily: 'Futura, sans-serif', fontSize: 80, color: '#0066FF', textAlign: 'center' as const, textBaseline: 'middle' as const },
        transform: { x: 0, y: 0, scale: 1, rotation: 0 },
        opacity: 1,
        fadeInDuration: 0.5,
        fadeOutDuration: 0.5,
        volume: 1,
    } as Clip,
];

interface UseEditorCoreOptions {
    /** Base path for navigation (e.g. '/pro' or '/') */
    basePath?: string;
}

export function useEditorCore(options: UseEditorCoreOptions = {}) {
    const { basePath = '/pro' } = options;

    // Routing
    const { projectId: urlProjectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();

    // Auth
    const [user, setUser] = useState<firebase.User | null>(null);

    // UI state
    const [activeTab, setActiveTab] = useState('assistant');
    const [isInitialDropzoneVisible, setIsInitialDropzoneVisible] = useState(false);
    const [projectLoadingState, setProjectLoadingState] = useState<{ isLoading: boolean; thumbnailUrl?: string }>({ isLoading: false });
    const [scaleFactor, setScaleFactor] = useState(1);
    const [selectedSubtitleId, setSelectedSubtitleId] = useState<number | string | null>(null);

    // Timeline player ref
    const timelinePlayerRef = useRef<{
        seek: (time: number) => void;
        getCurrentTime: () => number;
        pause?: () => void;
    }>(null);
    const projectLoadSequenceRef = useRef(0);
    const pendingProjectClipLoadTimerRef = useRef<number | null>(null);
    const pendingProjectOverlayTimerRef = useRef<number | null>(null);

    // ============================================================================
    // COMPOSE EXISTING HOOKS
    // ============================================================================

    const {
        allClips, setAllClips,
        tracks, setTracks,
        selectedClipId, setSelectedClipId,
        isClipContextMenuOpen, setIsClipContextMenuOpen,
        handleClipSelect,
        history, historyIndex, undo, redo,
        navigationStack, setNavigationStack,
        currentScope,
        updateClips: baseUpdateClips,
        cachedSourceClips, setCachedSourceClips,
        INITIAL_TRACKS
    } = useClipManagement();

    const {
        messages, setMessages,
        editingSuggestions, setEditingSuggestions,
        addUserMessage, addAssistantMessage, updateMessage
    } = useChat();

    const [montageVersions, setMontageVersions] = useState<MontageVersion[]>([]);
    const [currentVersionId, setCurrentVersionId] = useState<string | null>(null);
    const montageVersionsRef = useRef<MontageVersion[]>([]);
    const currentVersionIdStateRef = useRef<string | null>(null);
    const autoMontageForkBypassRef = useRef(false);

    useEffect(() => {
        montageVersionsRef.current = montageVersions;
    }, [montageVersions]);

    useEffect(() => {
        currentVersionIdStateRef.current = currentVersionId;
    }, [currentVersionId]);

    const runWithoutAutoMontageFork = useCallback((callback: () => void) => {
        autoMontageForkBypassRef.current = true;
        try {
            callback();
        } finally {
            window.setTimeout(() => {
                autoMontageForkBypassRef.current = false;
            }, 0);
        }
    }, []);

    const updateClips = useCallback((
        updaterOrValue: React.SetStateAction<Clip[]>,
        saveToHistory: boolean = true
    ) => {
        if (
            autoMontageForkBypassRef.current ||
            !saveToHistory ||
            navigationStack.length > 0 ||
            allClips.length === 0
        ) {
            baseUpdateClips(updaterOrValue, saveToHistory);
            return;
        }

        const nextClips = typeof updaterOrValue === 'function'
            ? (updaterOrValue as (prev: Clip[]) => Clip[])(allClips)
            : updaterOrValue;

        if (getMontageFingerprint(nextClips) === getMontageFingerprint(allClips)) {
            baseUpdateClips(nextClips, saveToHistory);
            return;
        }

        const activeVersion = montageVersionsRef.current.find(
            version => version.id === currentVersionIdStateRef.current
        ) || null;

        if (!currentVersionIdStateRef.current || isOriginalMontageVersion(activeVersion)) {
            const { nextVersions, nextVersionId } = buildAutoMontageForkPlan(
                montageVersionsRef.current,
                allClips
            );

            montageVersionsRef.current = nextVersions;
            currentVersionIdStateRef.current = nextVersionId;
            setMontageVersions(nextVersions);
            setCurrentVersionId(nextVersionId);
        }

        baseUpdateClips(nextClips, saveToHistory);
    }, [allClips, baseUpdateClips, navigationStack.length]);

    const [subtitleStyle, setSubtitleStyle] = useState<SubtitleStyle>({
        fontFamily: 'Arial, sans-serif',
        fontSize: 30,
        color: '#FFFFFF',
        shadowType: 'outline',
        positionY: 10,
        padding: 5,
    });
    const [activeSubtitleLanguage, setActiveSubtitleLanguage] = useState('fr');
    const lastAutoSubLangProjectRef = useRef<string | null>(null);

    const exportedVideosRef = useRef<any[]>([]);

    const {
        currentProjectId, setCurrentProjectId,
        currentProjectIdRef,
        savedProjects, setSavedProjects,
        folders, setFolders,
        rootProjects, setRootProjects,
        sidebarOpen, setSidebarOpen,
        markProjectDeleted
    } = useProjectState({
        user,
        allClips,
        tracks,
        montageVersions,
        messages,
        editingSuggestions,
        exportedVideosRef
    });

    // Auto-select first available subtitle language when project loads
    useEffect(() => {
        if (!currentProjectId || currentProjectId === lastAutoSubLangProjectRef.current) return;

        const clipWithSubs = allClips.find(c => c.subtitles && Object.keys(c.subtitles).length > 0);
        if (!clipWithSubs || !clipWithSubs.subtitles) return;

        const availableLangs = Object.keys(clipWithSubs.subtitles).filter(
            lang => clipWithSubs.subtitles![lang] && clipWithSubs.subtitles![lang].length > 0
        );
        if (availableLangs.length === 0) return;

        const priority = ['fr', 'en', 'es', 'de', 'it', 'pt'];
        const bestLang = priority.find(l => availableLangs.includes(l)) || availableLangs[0];

        setActiveSubtitleLanguage(bestLang);
        lastAutoSubLangProjectRef.current = currentProjectId;
        console.log(`[EditorCore] Auto-selected subtitle language: ${bestLang} (available: ${availableLangs.join(', ')})`);
    }, [allClips, currentProjectId]);

    const {
        isExporting, setIsExporting,
        exportStatus, setExportStatus,
        exportProgress, setExportProgress,
        exportDetails, setExportDetails,
        exportedVideos, setExportedVideos,
        exportAspectRatio, setExportAspectRatio,
        scaleMode, setScaleMode,
        vfxSegmentPreviews, setVfxSegmentPreviews,
        serverExportStatus,
        activeServerExports,
        exportManagerRef,
        serverExportUnsubscribesRef,
        resetExportState,
        isTimelineEligibleForFastExport,
        handleStartExport,
        handleCancelExport,
        handleStartServerExport,
        handleCancelServerExport,
        handleDeleteExport,
        checkActiveExportJobs,
        uploadAssetToStorage,
        exportStatusByProject,
        chunks,
        isMerging,
        retryChunkDownload,
        handleStartHybridExport,
        hasTimelineOverlays,
        totalTimelineDuration,
    } = useExportManager({
        allClips,
        tracks,
        activeSubtitleLanguage,
        subtitleStyle,
        user,
        currentProjectId,
        currentProjectIdRef,
        savedProjects,
        setSavedProjects,
        updateClips
    });

    // Sync exportedVideos ref for auto-save (breaks circular dep with useProjectState)
    useEffect(() => {
        exportedVideosRef.current = exportedVideos;
    }, [exportedVideos]);

    // Additional state
    const [translationsInProgress, setTranslationsInProgress] = useState<Set<string>>(new Set());
    const [generatedVoices, setGeneratedVoices] = useState<GeneratedVoice[]>([]);
    const [voiceoverText, setVoiceoverText] = useState("");
    const [useGpt4oTranscribe, setUseGpt4oTranscribe] = useState(() => {
        return localStorage.getItem('transcriptionMode') === 'gpt4o';
    });
    const [currentMontageElements, setCurrentMontageElements] = useState<MontageElements>({
        intro: false, outro: false, synthe: false, panel: false
    });

    // ============================================================================
    // DERIVED STATE
    // ============================================================================

    const selectedClip = useMemo(() => {
        return currentScope.clips.find((c: Clip) => c.id === selectedClipId) || null;
    }, [currentScope.clips, selectedClipId]);

    // ============================================================================
    // AUTH INITIALIZATION
    // ============================================================================

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
            setUser(currentUser);

            if (currentUser) {
                setActiveTab('assistant');
                setMessages([{
                    id: 'gemini-status-loading',
                    role: 'assistant',
                    text: "Connexion à Vertex AI..."
                }]);

                try {
                    const result: firebase.functions.HttpsCallableResult = await callGemini({ model: 'ping' });
                    if ((result.data as any)?.status === 'ready') {
                        setMessages(prev => {
                            if (prev.some(m => m.id === 'gemini-status-ready')) {
                                return prev.filter(m => m.id !== 'gemini-status-loading');
                            }
                            return [
                                ...prev.filter(m => m.id !== 'gemini-status-loading'),
                                {
                                    id: 'gemini-status-ready',
                                    role: 'assistant',
                                    text: "Connecté à Vertex AI. Je suis prêt ! Que souhaitez-vous créer aujourd'hui ?"
                                }
                            ];
                        });
                    } else {
                        throw new Error("Invalid response from ping");
                    }
                } catch (error) {
                    console.error("Gemini health check failed:", error);
                    setMessages(prev => [
                        ...prev.filter(m => m.id !== 'gemini-status-loading'),
                        {
                            id: 'gemini-status-error',
                            role: 'assistant',
                            text: "⚠️ Les fonctionnalités d'IA (sous-titres, montage, voix-off) OUT"
                        }
                    ]);
                }

                // Preload ffmpeg
                getConverter().load().catch(console.error);
                FFmpegService.preload().catch(console.error);
            } else {
                setActiveTab('account');
                setMessages([
                    { id: 1, role: "assistant", text: "Bonjour ! Connectez-vous pour commencer." }
                ]);
            }
        });
        return () => unsubscribe();
    }, []);

    // ============================================================================
    // AUTO Effects (from App.tsx)
    // ============================================================================

    // Track thumbnailAttemptedRef for auto-regeneration
    const thumbnailAttemptedRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        setIsInitialDropzoneVisible(
            allClips.filter(c => c.type === 'video' || c.type === 'image' || c.type === 'audio').length === 0
        );

        allClips.forEach(clip => {
            const hasValidUrl = clip.url && !clip.url.startsWith('file-requirement:');
            const needsThumbnails = clip.type === 'video' && (!clip.thumbnails || clip.thumbnails.length === 0);
            const alreadyAttempted = thumbnailAttemptedRef.current.has(clip.id);
            const isTooLong = clip.originalDuration && clip.originalDuration > 1800;

            if (hasValidUrl && needsThumbnails && !alreadyAttempted && !isTooLong) {
                thumbnailAttemptedRef.current.add(clip.id);
                generateThumbnails(clip.url!).then(thumbnails => {
                    if (thumbnails && thumbnails.length > 0) {
                        setAllClips(prev => prev.map(c => c.id === clip.id ? { ...c, thumbnails } : c));
                    }
                }).catch(console.warn);
            }
        });
    }, [allClips]);

    // Auto-show tracks for clips on t1/s1
    useEffect(() => {
        const hasT1Clips = allClips.some(c => c.trackId === 't1');
        const hasS1Clips = allClips.some(c => c.trackId === 's1');

        setTracks(prev => prev.map(track => {
            if (track.id === 't1' && hasT1Clips && !track.isVisible) return { ...track, isVisible: true };
            if (track.id === 's1' && hasS1Clips && !track.isVisible) return { ...track, isVisible: true };
            return track;
        }));
    }, [allClips]);

    // Sync clips to montage version
    useEffect(() => {
        if (currentVersionId) {
            setMontageVersions(prev => prev.map(v =>
                v.id === currentVersionId ? { ...v, clips: allClips } : v
            ));
        }
    }, [allClips, currentVersionId]);

    // Auto-switch to Master tab in restricted mode
    useEffect(() => {
        if (currentScope?.type === 'synthe' || currentScope?.type === 'panel') {
            setActiveTab('master');
        }
    }, [currentScope?.id, currentScope?.type]);

    // Blob cleanup on unmount
    useEffect(() => {
        return () => {
            const clipBlobUrls = new Set<string>();
            allClips.forEach(clip => {
                const localUrls = [clip.url, clip.localUrl].filter((url): url is string => !!url && url.startsWith('blob:'));
                localUrls.forEach(url => clipBlobUrls.add(url));
                if (clip.thumbnails) clip.thumbnails.forEach(thumb => {
                    if (thumb && thumb.startsWith('blob:')) URL.revokeObjectURL(thumb);
                });
            });
            clipBlobUrls.forEach(url => URL.revokeObjectURL(url));
        };
    }, []);

    // ============================================================================
    // BUSINESS LOGIC — Extracted from App.tsx
    // ============================================================================

    const handleClipVolumeChange = useCallback((clipId: string, volume: number, saveToHistory: boolean = false) => {
        updateClips(prev => prev.map(c => c.id === clipId ? { ...c, volume } : c), saveToHistory);
    }, [updateClips]);

    const handleSelectAudioSource = useCallback((clip: Clip, sourceType: 'original' | 'enhanced') => {
        updateClips(prev => prev.map(c => {
            if (c.id === clip.id) {
                const updated = { ...c, activeAudioSource: sourceType };
                // If it's a pure audio clip, update the main URL for the timeline
                if (c.type === 'audio' && c.audioSources) {
                    const source = c.audioSources.find(s => s.type === sourceType);
                    if (source) updated.url = source.url;
                }
                return updated;
            }
            return c;
        }), false);
    }, [updateClips]);

    const updateClipSafe = useCallback((targetProjectId: string, clipId: string, updater: (c: Clip) => Clip) => {
        const updateMontageVersions = (versions?: MontageVersion[]) =>
            versions?.map(version => ({
                ...version,
                clips: updateClipDeep(version.clips || [], clipId, updater)
            }));

        setSavedProjects(prev => {
            return prev.map(p => {
                if (p.id === targetProjectId) {
                    const newClips = updateClipDeep(p.clips, clipId, updater);
                    const newMontageVersions = updateMontageVersions(p.montageVersions);
                    const updatedProject = {
                        ...p,
                        clips: newClips,
                        ...(newMontageVersions ? { montageVersions: newMontageVersions } : {}),
                        updatedAt: Date.now()
                    };
                    saveProjectToFirestore(updatedProject).catch(err =>
                        console.error(`[UpdateClipSafe] Failed to save project ${targetProjectId}`, err)
                    );
                    return updatedProject;
                }
                return p;
            });
        });

        if (currentProjectIdRef.current === targetProjectId) {
            updateClips(prevClips => updateClipDeep(prevClips, clipId, updater), false);
        }
    }, [updateClips]);

    const onClipSelect = useCallback((clip: Clip | null) => {
        handleClipSelect(clip);
        if (clip) {
            const isAudioClip = clip.type === 'audio' || (clip.type === 'video' && !clip.url?.toLowerCase().endsWith('.gif'));
            if (isAudioClip) {
                setIsClipContextMenuOpen(true);
                setActiveTab('assistant');
            }
        }
    }, [handleClipSelect, setIsClipContextMenuOpen, setActiveTab]);

    const handleCloseClipContextMenu = useCallback(() => {
        setIsClipContextMenuOpen(false);
        setActiveTab('assistant');
    }, []);

    // --- Seek ---
    const handleSeekRequest = useCallback((time: number) => {
        if (timelinePlayerRef.current) {
            timelinePlayerRef.current.seek(time);
        }
    }, []);

    // --- Save project ---
    const handleManualSave = useCallback(async () => {
        if (!currentProjectId) return;

        const project = savedProjects.find(p => p.id === currentProjectId);
        if (!project) return;

        // Upload blobs in the full tree before saving
        const uploadMediaRecursive = async (clips: Clip[]): Promise<Clip[]> => {
            return await Promise.all(clips.map(async (clip) => {
                let updatedClip = { ...clip };

                if (updatedClip.syntheData && updatedClip.syntheData.clips) {
                    updatedClip.syntheData = {
                        ...updatedClip.syntheData,
                        clips: await uploadMediaRecursive(updatedClip.syntheData.clips)
                    };
                }

                if (updatedClip.url && updatedClip.url.startsWith('blob:')) {
                    try {
                        console.log(`[ManualSave] Uploading ${updatedClip.name} to R2...`);
                        const response = await fetch(updatedClip.url);
                        const blob = await response.blob();

                        let extension = '';
                        const type = blob.type || '';
                        if (type.includes('image')) extension = '.png';
                        else if (type.includes('video')) extension = '.mp4';
                        else if (type.includes('audio')) extension = '.wav';

                        const fileName = updatedClip.name.includes('.') ? updatedClip.name : `${updatedClip.name}${extension}`;
                        const fileHash = `${updatedClip.name}_${blob.size}_${blob.type}`.replace(/[^a-zA-Z0-9_.-]/g, '_');

                        let mimeType = blob.type || (fileName.endsWith('.mp4') ? 'video/mp4' :
                            (fileName.endsWith('.png') ? 'image/png' :
                                (fileName.endsWith('.wav') ? 'audio/wav' :
                                    (fileName.endsWith('.mp3') ? 'audio/mpeg' : 'application/octet-stream'))));

                        if (mimeType.includes(';')) mimeType = mimeType.split(';')[0];

                        const uploadConfig = await getR2UploadUrl({ fileName, contentType: mimeType, fileHash });
                        const configData = uploadConfig.data as { uploadUrl?: string; publicUrl: string; existing?: boolean };

                        if (configData.existing) {
                            const localUrl = getClipLocalSourceUrl(updatedClip);
                            if (localUrl) {
                                updatedClip.localUrl = localUrl;
                            }
                            updatedClip.url = configData.publicUrl;
                            return updatedClip;
                        }

                        const { uploadUrl, publicUrl } = configData as { uploadUrl: string; publicUrl: string };

                        await new Promise<void>((resolve, reject) => {
                            const xhr = new XMLHttpRequest();
                            xhr.open('PUT', uploadUrl, true);
                            xhr.setRequestHeader('Content-Type', mimeType);
                            xhr.onload = () => {
                                if (xhr.status >= 200 && xhr.status < 300) resolve();
                                else reject(new Error(`Upload failed with status ${xhr.status}`));
                            };
                            xhr.onerror = () => reject(new Error('Network error during upload'));
                            xhr.send(blob);
                        });

                        const localUrl = getClipLocalSourceUrl(updatedClip);
                        if (localUrl) {
                            updatedClip.localUrl = localUrl;
                        }
                        updatedClip.url = publicUrl;
                        console.log(`[ManualSave] Uploaded ${updatedClip.name} -> ${publicUrl}`);
                    } catch (e) {
                        console.error(`[ManualSave] Failed to upload ${updatedClip.name}`, e);
                    }
                }
                return updatedClip;
            }));
        };

        const finalUploadedClips = await uploadMediaRecursive(allClips);

        const newMontageVersions = await Promise.all(
            montageVersions.map(async (mv) => ({
                ...mv,
                clips: await uploadMediaRecursive(mv.clips)
            }))
        );

        setAllClips(finalUploadedClips);

        const updatedProject: SavedProject = {
            ...project,
            clips: finalUploadedClips,
            tracks: navigationStack.length === 0 ? tracks : project.tracks,
            montageVersions: newMontageVersions,
            messages: messages.filter(m => !m.isLoading),
            aiSuggestions: editingSuggestions,
            exports: exportedVideos.map(v => ({
                id: v.id,
                name: v.name,
                urlMP4: v.urlMP4,
                urlWebM: v.urlWebM,
                thumbnailUrl: v.thumbnailUrl
            })),
            updatedAt: Date.now()
        };

        try {
            await saveProjectToFirestore(updatedProject);
            console.log('[EditorCore] Manual Save success');
            setSavedProjects(prev => prev.map(p => p.id === currentProjectId ? updatedProject : p));
        } catch (e) {
            console.error('[EditorCore] Manual Save failed', e);
        }
    }, [currentProjectId, savedProjects, allClips, tracks, navigationStack, montageVersions, messages, editingSuggestions, exportedVideos]);

    const applyHydratedClipData = useCallback((targetClips: Clip[], hydratedClips: Clip[]): Clip[] => {
        const hasSubtitlePayload = (clip?: Clip) =>
            !!clip && (
                !!clip.subtitles ||
                !!clip.subtitlesUrl ||
                !!(clip as any).subtitlesHash ||
                !!(clip as any)._compactSubtitles
            );

        const hydratedById = new Map<string, Clip>();
        for (const hydratedClip of flattenClipsDeep(hydratedClips)) {
            const existing = hydratedById.get(hydratedClip.id);
            if (!existing || (hasSubtitlePayload(hydratedClip) && !hasSubtitlePayload(existing))) {
                hydratedById.set(hydratedClip.id, hydratedClip);
            }
        }

        return mapClipsDeep(targetClips, clip => {
            const hydratedClip = hydratedById.get(clip.id);
            if (!hydratedClip) return clip;

            return {
                ...clip,
                subtitles: hydratedClip.subtitles,
                subtitlesUrl: hydratedClip.subtitlesUrl,
                subtitlesHash: (hydratedClip as any).subtitlesHash,
                _compactSubtitles: (hydratedClip as any)._compactSubtitles,
            } as Clip;
        });
    }, []);

    const collectProjectClips = useCallback((project?: Pick<SavedProject, 'clips' | 'montageVersions'> | null): Clip[] => {
        if (!project) return [];
        return [
            ...(project.clips || []),
            ...(project.montageVersions?.flatMap(version => version.clips || []) || [])
        ];
    }, []);

    const mergeHydratedClipDataIntoVersions = useCallback((versions: MontageVersion[], hydratedClips: Clip[]): MontageVersion[] => {
        return versions.map(version => ({
            ...version,
            clips: applyHydratedClipData(version.clips || [], hydratedClips)
        }));
    }, [applyHydratedClipData]);

    const hydrateProjectSubtitles = useCallback(async (project: SavedProject): Promise<SavedProject> => {
        const projectClips = collectProjectClips(project);
        const clipsNeedingSubs = projectClips.filter((c: any) => !c.subtitles && (c.subtitlesUrl || c._compactSubtitles));
        if (clipsNeedingSubs.length === 0) {
            return project;
        }

        const { loadSubtitlesForProject } = await import('../firestore');
        const hydratedProject = await loadSubtitlesForProject(JSON.parse(JSON.stringify(project)) as SavedProject);
        const hydratedClips = collectProjectClips(hydratedProject);

        setSavedProjects(prev => prev.map(p => {
            if (p.id !== project.id) return p;
            return {
                ...p,
                clips: applyHydratedClipData(p.clips || [], hydratedClips),
                ...(p.montageVersions
                    ? { montageVersions: mergeHydratedClipDataIntoVersions(p.montageVersions, hydratedClips) }
                    : {})
            };
        }));

        if (currentProjectIdRef.current === project.id) {
            updateClips(prev => applyHydratedClipData(prev, hydratedClips), false);
            setMontageVersions(prev => {
                const next = mergeHydratedClipDataIntoVersions(prev, hydratedClips);
                montageVersionsRef.current = next;
                return next;
            });
        }

        return hydratedProject;
    }, [applyHydratedClipData, collectProjectClips, currentProjectIdRef, mergeHydratedClipDataIntoVersions, setSavedProjects, updateClips]);

    const hydrateProjectLocalCache = useCallback(async (projectId: string) => {
        if (currentProjectIdRef.current !== projectId) return;

        const projectSnapshot = savedProjects.find(project => project.id === projectId);
        const clipsToScan = projectSnapshot
            ? collectProjectClips(projectSnapshot)
            : allClips;
        const remoteCandidates = getRemoteVideoClipsNeedingLocalCache(clipsToScan);
        if (remoteCandidates.length === 0) {
            setMessages(prev => [...prev, {
                id: Date.now(),
                role: 'assistant',
                text: "Aucune vidéo distante à mettre en cache local pour cette session."
            }]);
            return;
        }

        const loadingId = `local-cache-${Date.now()}`;
        setMessages(prev => [
            ...prev.filter(m => m.pendingChoice?.type !== 'local-cache-choice'),
            {
                id: loadingId,
                role: 'assistant',
                text: '',
                isLoading: true,
                statusText: 'Mise en cache locale des vidéos...',
                progress: 0
            }
        ]);

        const remoteSources = Array.from(new Set(
            remoteCandidates
                .map(clip => getClipSourceUrl(clip, { preferLocal: false, includeStreamUrl: true, allowStreamingManifest: false }))
                .filter((url): url is string => !!url)
        ));

        const localUrlBySource = new Map<string, string>();
        let successCount = 0;
        let failedCount = 0;

        for (let index = 0; index < remoteSources.length; index++) {
            const sourceUrl = remoteSources[index];
            if (currentProjectIdRef.current !== projectId) return;

            try {
                const response = await fetch(sourceUrl);
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const blob = await response.blob();
                localUrlBySource.set(sourceUrl, URL.createObjectURL(blob));
                successCount++;
            } catch (error) {
                failedCount++;
                console.warn(`[LocalCache] Failed to cache ${sourceUrl}`, error);
            }

            const progress = Math.round(((index + 1) / remoteSources.length) * 100);
            setMessages(prev => prev.map(m =>
                m.id === loadingId ? { ...m, progress, statusText: `Mise en cache locale des vidéos... ${index + 1}/${remoteSources.length}` } : m
            ));
        }

        if (currentProjectIdRef.current !== projectId) return;

        if (localUrlBySource.size > 0) {
            const attachLocalUrls = (clips: Clip[]) => mapClipsDeep(clips, clip => {
                if (clip.type !== 'video') return clip;
                const sourceUrl = getClipSourceUrl(clip, { preferLocal: false, includeStreamUrl: true, allowStreamingManifest: false });
                const localUrl = sourceUrl ? localUrlBySource.get(sourceUrl) : undefined;
                if (!localUrl || clip.localUrl === localUrl) return clip;
                return { ...clip, localUrl };
            });

            updateClips(prev => attachLocalUrls(prev));
            setSavedProjects(prev => prev.map(project => {
                if (project.id !== projectId) return project;
                return {
                    ...project,
                    clips: attachLocalUrls(project.clips || []),
                    montageVersions: project.montageVersions?.map(version => ({
                        ...version,
                        clips: attachLocalUrls(version.clips || [])
                    })) || []
                };
            }));
        }

        const completionMessage = successCount > 0
            ? `Cache local prêt pour ${successCount}/${remoteSources.length} vidéo(s). Le preview et l'export client utiliseront la source locale pendant cette session.`
            : `Le cache local n'a pas pu être préparé${failedCount > 0 ? ` (${failedCount} échec(s))` : ''}.`;

        setMessages(prev => [
            ...prev.filter(m => m.id !== loadingId),
            { id: Date.now(), role: 'assistant', text: completionMessage }
        ]);
    }, [allClips, collectProjectClips, currentProjectIdRef, savedProjects, setMessages, setSavedProjects, updateClips]);

    // --- Load project ---
    const loadProject = useCallback((project: SavedProject) => {
        const loadSequence = ++projectLoadSequenceRef.current;

        if (pendingProjectClipLoadTimerRef.current !== null) {
            window.clearTimeout(pendingProjectClipLoadTimerRef.current);
            pendingProjectClipLoadTimerRef.current = null;
        }
        if (pendingProjectOverlayTimerRef.current !== null) {
            window.clearTimeout(pendingProjectOverlayTimerRef.current);
            pendingProjectOverlayTimerRef.current = null;
        }

        setProjectLoadingState({ isLoading: true, thumbnailUrl: project.thumbnailUrl });

        if (serverExportUnsubscribesRef.current) {
            serverExportUnsubscribesRef.current.forEach(unsub => unsub());
            serverExportUnsubscribesRef.current.clear();
        }

        if (timelinePlayerRef.current?.pause) {
            timelinePlayerRef.current.pause();
        }
        if (timelinePlayerRef.current) {
            timelinePlayerRef.current.seek(0);
        }

        setNavigationStack([]);
        setCachedSourceClips([]);
        setVfxSegmentPreviews([]);
        currentVersionIdStateRef.current = null;
        setCurrentVersionId(null);
        setSelectedClipId(null);
        setIsClipContextMenuOpen(false);

        runWithoutAutoMontageFork(() => {
            updateClips(() => []);
        });
        const cacheableRemoteVideos = getRemoteVideoClipsNeedingLocalCache([
            ...project.clips,
            ...(project.montageVersions?.flatMap(version => version.clips || []) || [])
        ]);

        pendingProjectClipLoadTimerRef.current = window.setTimeout(() => {
            if (projectLoadSequenceRef.current !== loadSequence) return;
            runWithoutAutoMontageFork(() => {
                updateClips(() => project.clips);
            });
            pendingProjectClipLoadTimerRef.current = null;
        }, 50);

        const needsTrackMigration = !project.tracks ||
            project.tracks.some(t => t.id === 'a1' || t.label === 'Principal') ||
            project.tracks.length !== INITIAL_TRACKS.length;

        if (needsTrackMigration) {
            setTracks(INITIAL_TRACKS.map(t => ({ ...t })));
        } else {
            setTracks(project.tracks);
        }

        setMontageVersions(project.montageVersions || []);
        montageVersionsRef.current = project.montageVersions || [];
        if (project.montageVersions?.length) {
            const lastVersion = project.montageVersions[project.montageVersions.length - 1];
            currentVersionIdStateRef.current = lastVersion.id;
            setCurrentVersionId(lastVersion.id);
        }

        if (project.exports?.length) {
            setExportedVideos(project.exports.map(e => ({
                id: e.id || `export-${Date.now()}`,
                name: e.name,
                urlWebM: e.urlWebM || e.url || '',
                urlMP4: e.urlMP4,
                thumbnailUrl: project.thumbnailUrl || e.thumbnailUrl,
            })));
        } else {
            setExportedVideos([]);
        }

        setEditingSuggestions(project.aiSuggestions || []);

        if (project.generatedVoices?.length) {
            setGeneratedVoices(project.generatedVoices.map(v => ({
                id: v.id, text: v.text, voice: v.voice, url: v.url, duration: v.duration, isLoading: false
            })));
        } else {
            setGeneratedVoices([]);
        }
        setVoiceoverText(project.voiceoverText || '');

        const baseMessages = project.messages?.length
            ? project.messages.filter(message => message.pendingChoice?.type !== 'local-cache-choice')
            : [{ id: Date.now(), role: 'assistant', text: `Projet "${project.name}" chargé ! Comment puis-je vous aider ?` }];

        setMessages(cacheableRemoteVideos.length > 0 ? [
            ...baseMessages,
            {
                id: `local-cache-choice-${Date.now()}`,
                role: 'assistant',
                text: `Ce projet contient ${cacheableRemoteVideos.length} vidéo(s) distante(s). Voulez-vous créer un cache local pour cette session afin de réduire le lag en preview et en export ?`,
                pendingChoice: {
                    type: 'local-cache-choice',
                    projectId: project.id,
                    options: [
                        { label: 'Yes', value: 'yes' },
                        { label: 'No', value: 'no' }
                    ]
                }
            }
        ] : baseMessages);

        setCurrentProjectId(project.id);

        pendingProjectOverlayTimerRef.current = window.setTimeout(() => {
            if (projectLoadSequenceRef.current !== loadSequence) return;
            setProjectLoadingState({ isLoading: false });
            pendingProjectOverlayTimerRef.current = null;
        }, 1200);

        checkActiveExportJobs(project.id);

        // Async subtitle loading (from R2 or inline fallback)
        (async () => {
            const clipsNeedingSubs = collectProjectClips(project).filter((c: any) =>
                !c.subtitles && (c.subtitlesUrl || c._compactSubtitles)
            );
            if (clipsNeedingSubs.length > 0) {
                await hydrateProjectSubtitles(project);
                if (projectLoadSequenceRef.current !== loadSequence) return;
            }
        })();
    }, [updateClips, setTracks, checkActiveExportJobs, collectProjectClips, hydrateProjectSubtitles]);

    // URL-based project loading
    useEffect(() => {
        if (urlProjectId && urlProjectId !== currentProjectId && user) {
            (async () => {
                const localProject = savedProjects.find(p => p.id === urlProjectId);
                if (localProject) {
                    loadProject(localProject);
                    return;
                }
                const remoteProject = await getProjectById(urlProjectId);
                if (remoteProject) {
                    loadProject(remoteProject);
                    setSavedProjects(prev => [...prev, remoteProject]);
                } else {
                    setMessages(prev => [...prev, {
                        id: Date.now(),
                        role: 'assistant',
                        text: `⚠️ Projet "${urlProjectId}" introuvable.`
                    }]);
                    setCurrentProjectId(null);
                    navigate(basePath);
                }
            })();
        }
    }, [urlProjectId, currentProjectId, user, savedProjects, loadProject, navigate, basePath]);

    // Keep URL in sync with selected project (better back/refresh behavior)
    useEffect(() => {
        if (!user || !currentProjectId) return;
        if (urlProjectId === currentProjectId) return;
        if (!urlProjectId) {
            navigate(`${basePath}/project/${currentProjectId}`, { replace: true });
        }
    }, [user, currentProjectId, urlProjectId, navigate, basePath]);

    // --- Montage versions ---
    const addMontageVersion = useCallback(async (clips: Clip[], prompt: string, name?: string) => {
        const versionId = `version-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        let newVersions: MontageVersion[] = [];

        setMontageVersions(prev => {
            const montageCount = prev.filter(v => v.name.startsWith('Montage')).length;
            const versionName = name || `Montage ${montageCount + 1}`;
            const clonedClips = clips.map(c => ({ ...c }));
            const newVersion = { id: versionId, name: versionName, clips: clonedClips, prompt };
            newVersions = [...prev, newVersion];
            montageVersionsRef.current = newVersions;
            return newVersions;
        });

        currentVersionIdStateRef.current = versionId;
        setCurrentVersionId(versionId);

        if (currentProjectId && user) {
            try {
                const projectBase = savedProjects.find(p => p.id === currentProjectId);
                if (!projectBase) return versionId;

                const projectToSave = {
                    id: currentProjectId,
                    name: projectBase.name || 'Untitled Project',
                    clips: allClips,
                    tracks: tracks,
                    montageVersions: newVersions,
                    lastModified: Date.now(),
                    ownerId: user.uid,
                    messages: messages.filter(m => !m.isLoading),
                    editingSuggestions: editingSuggestions,
                    exportedVideos: exportedVideos,
                    createdAt: projectBase.createdAt || Date.now()
                };

                await saveProjectToFirestore(projectToSave as any);
                setSavedProjects(prev => prev.map(p => p.id === currentProjectId ? { ...p, montageVersions: newVersions } as any : p));
            } catch (err) {
                console.error("Failed to save montage version:", err);
            }
        }

        return versionId;
    }, [currentProjectId, user, allClips, tracks, savedProjects, messages, editingSuggestions, exportedVideos]);

    const ensureBaseVersion = useCallback((clips: Clip[]) => {
        if (montageVersions.length === 0 && clips.length > 0) {
            addMontageVersion(clips, 'Clips originaux', 'Version originale');
        }
    }, [addMontageVersion, montageVersions.length]);

    const handleSelectVersion = useCallback((versionId: string) => {
        const targetVersion = montageVersions.find(v => v.id === versionId);
        if (!targetVersion) return;
        runWithoutAutoMontageFork(() => {
            currentVersionIdStateRef.current = versionId;
            setCurrentVersionId(versionId);
            updateClips(() => targetVersion.clips.map(c => ({ ...c })));
        });
    }, [montageVersions, runWithoutAutoMontageFork, updateClips]);

    // --- Navigation (synthe/panel) ---
    const navigateIntoClip = useCallback((clip: Clip) => {
        if (clip.type === 'synthe' || clip.type === 'panel') {
            if (!clip.syntheData) {
                const defaultClipsWithUniqueIds = clip.type === 'synthe'
                    ? DEFAULT_SYNTHE_CLIPS.map((c, idx) => ({
                        ...c,
                        id: `synthe-text-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`
                    }))
                    : DEFAULT_PANEL_CLIPS.map((c, idx) => ({
                        ...c,
                        id: `panel-text-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`
                    }));

                const initializedClip = {
                    ...clip,
                    syntheData: {
                        clips: defaultClipsWithUniqueIds,
                        tracks: INITIAL_TRACKS,
                        backgroundColor: clip.type === 'panel' ? '#000000' : undefined
                    }
                };
                updateClips((prev: Clip[]) => prev.map((c: Clip) => c.id === clip.id ? initializedClip : c));
                setNavigationStack((prev: Clip[]) => [...prev, initializedClip]);
            } else {
                setNavigationStack((prev: Clip[]) => [...prev, clip]);
            }
            setSelectedClipId(null);
            timelinePlayerRef.current?.seek(0);
        }
    }, [updateClips]);

    const navigateUp = useCallback(() => {
        setNavigationStack((prev: Clip[]) => prev.slice(0, -1));
        setSelectedClipId(null);
        timelinePlayerRef.current?.seek(0);
    }, []);

    // --- Delete project ---
    const handleDeleteProject = useCallback(async (projectId: string) => {
        markProjectDeleted(projectId); // Prevent auto-save race
        try {
            await deleteProjectFromFirestore(projectId);
            setSavedProjects(prev => prev.filter(p => p.id !== projectId));
            setRootProjects(prev => prev.filter(p => p.id !== projectId));
            setFolders(prev => prev.map(f => ({
                ...f,
                projects: f.projects.filter(p => p.id !== projectId)
            })));

            // If we deleted the currently open project, reset editor and route.
            if (currentProjectIdRef.current === projectId) {
                setCurrentProjectId(null);
                setNavigationStack([]);
                setCachedSourceClips([]);
                setVfxSegmentPreviews([]);
                currentVersionIdStateRef.current = null;
                setCurrentVersionId(null);
                setSelectedClipId(null);
                setIsClipContextMenuOpen(false);
                montageVersionsRef.current = [];
                setMontageVersions([]);
                setGeneratedVoices([]);
                setVoiceoverText('');
                runWithoutAutoMontageFork(() => {
                    updateClips(() => []);
                });
                setTracks(INITIAL_TRACKS.map(t => ({ ...t })));
                navigate(basePath);
            }
        } catch (error) {
            console.error('[DeleteProject] Failed:', error);
        }
    }, [markProjectDeleted, setSavedProjects, setRootProjects, setFolders, setCurrentProjectId, setNavigationStack, setCachedSourceClips, setVfxSegmentPreviews, setCurrentVersionId, setSelectedClipId, setIsClipContextMenuOpen, setMontageVersions, setGeneratedVoices, setVoiceoverText, updateClips, setTracks, INITIAL_TRACKS, navigate, basePath, currentProjectIdRef]);

    // --- Duplicate project ---
    const handleDuplicateProject = useCallback(async (project: SavedProject) => {
        try {
            if (!project) return;
            const newId = Date.now().toString();
            const newTitle = `${project.name} (Copy)`;
            const newProject: SavedProject = {
                ...project,
                id: newId,
                name: newTitle,
                updatedAt: Date.now(),
                createdAt: Date.now(),
                clips: JSON.parse(JSON.stringify(project.clips || [])),
                tracks: JSON.parse(JSON.stringify(project.tracks || [])),
            };
            await saveProjectToFirestore(newProject);
            setSavedProjects(prev => [newProject, ...prev]);
            setRootProjects(prev => [{
                id: newId,
                title: newTitle,
                assets: [],
                updated: 'just now',
                thumbnailUrl: project.thumbnailUrl,
            }, ...prev]);
        } catch (error) {
            console.error("Failed to duplicate project", error);
        }
    }, []);

    // --- Rename project ---
    const handleRenameProject = useCallback(async (project: SidebarProject, newName: string) => {
        try {
            if (!newName || newName.trim() === project.title) return;
            const title = newName.trim();
            const updatedDate = new Date().toLocaleDateString();

            setSavedProjects(prev => prev.map(p =>
                p.id === project.id ? { ...p, name: title, updatedAt: Date.now() } : p
            ));
            setRootProjects(prev => {
                if (prev.some(p => p.id === project.id)) {
                    return prev.map(p => p.id === project.id ? { ...p, title, updated: updatedDate } : p);
                }
                return prev;
            });
            setFolders(prev => prev.map(folder => {
                const projects = folder.projects || [];
                if (projects.some(c => c.id === project.id)) {
                    return {
                        ...folder,
                        projects: projects.map(c => c.id === project.id ? { ...c, title, updated: updatedDate } : c)
                    };
                }
                return folder;
            }));

            await renameProjectInFirestore(project.id, title);
        } catch (error) {
            console.error('[RenameProject] Failed:', error);
        }
    }, []);

    // --- Share project ---
    const [shareToast, setShareToast] = useState<{ type: 'success' | 'error'; message: string; url?: string } | null>(null);

    const handleShareProject = useCallback(async (project: SidebarProject) => {
        try {
            const fullProject = savedProjects.find(p => p.id === project.id);
            if (!fullProject) {
                setShareToast({ type: 'error', message: 'Projet introuvable.' });
                return;
            }

            // Retry logic for Firestore write
            let url: string | null = null;
            let lastError: any = null;
            for (let attempt = 0; attempt < 3; attempt++) {
                try {
                    url = await shareProject(fullProject);
                    break;
                } catch (err) {
                    lastError = err;
                    console.warn(`[ShareProject] Attempt ${attempt + 1} failed:`, err);
                    if (attempt < 2) await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
                }
            }

            if (!url) {
                throw lastError || new Error('Partage échoué après 3 tentatives');
            }

            // Copy to clipboard with fallback
            try {
                await navigator.clipboard.writeText(url);
            } catch {
                // Fallback for browsers that block clipboard API
                const textarea = document.createElement('textarea');
                textarea.value = url;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }

            // Show toast popup
            setShareToast({ type: 'success', message: 'Lien copié dans le presse-papier !', url });
            setTimeout(() => setShareToast(null), 8000);

            // Also send chat message
            setMessages(prev => [...prev, {
                id: Date.now(),
                role: 'assistant',
                text: `✅ Lien copié ! Partagez ce lien avec vos collaborateurs :\n\n${url}`
            }]);
        } catch (error) {
            console.error('[ShareProject] Failed:', error);
            setShareToast({ type: 'error', message: 'Erreur lors du partage. Réessayez.' });
            setTimeout(() => setShareToast(null), 5000);
        }
    }, [savedProjects]);

    // --- Subtitle click ---
    const handleSubtitleClick = useCallback((subtitle: Subtitle) => {
        setActiveTab('subtitles');
        const clip = allClips.find(c =>
            c.subtitles &&
            c.subtitles[activeSubtitleLanguage] &&
            c.subtitles[activeSubtitleLanguage].some(s => s.id === subtitle.id)
        );
        if (clip) {
            setSelectedClipId(clip.id);
            setSelectedSubtitleId(subtitle.id);
        }
    }, [allClips, activeSubtitleLanguage]);

    // --- Open export menu ---
    const handleOpenExportMenu = useCallback(() => {
        setActiveTab('export');
    }, []);


    // --- Generate editing suggestions ---
    const generateEditingSuggestions = useCallback(async (subtitles: Subtitle[]) => {
        try {
            const formatTimestamp = (seconds: number): string => {
                const mins = Math.floor(seconds / 60);
                const secs = Math.floor(seconds % 60);
                return `${mins}:${secs.toString().padStart(2, '0')}`;
            };

            const transcriptWithTimestamps = subtitles.map((sub, i) =>
                `[${formatTimestamp(sub.start)} - ${formatTimestamp(sub.end)}] ${sub.text}`
            ).join('\n');

            const payload = {
                model: 'gemini-3.5-flash',
                contents: [{
                    role: 'user',
                    parts: [
                        {
                            text: `Tu es un expert en montage vidéo LVMH spécialisé dans l'extraction de segments pour les réseaux sociaux.

TRANSCRIPTION AVEC TIMESTAMPS:
${transcriptWithTimestamps}

OBJECTIF: Analyser le contenu et proposer des SEGMENTS À COUPER de la vidéo originale.

TYPES DE SUGGESTIONS:

1. **SUJETS ISOLÉS** (2-4 suggestions)
   - Identifier des sujets/thèmes complets et auto-suffisants
   - Exemple: Introduction de la maison, explication d'un savoir-faire, présentation produit
   - Chaque segment doit raconter une histoire complète

2. **MONTAGES COURTS** (2-3 suggestions)
   - Extraits percutants pour réseaux sociaux (15-60s)
   - Highlight d'un moment clé
   - Citation marquante
   - Démonstration visuelle

3. **FORMATS SPÉCIFIQUES** (1-2 suggestions)
   - LinkedIn: segment professionnel/corporate
   - TikTok/Reels: moment viral/captivant
   - Teaser: meilleurs extraits en 30s

4. **SURCOUCHES (Synthés)** (1-2 suggestions)
   - Suggérer l'ajout d'un synthé pour identifier une personne qui parle
   - NE JAMAIS suggérer de 'panel' sauf si l'utilisateur le demande explicitement dans sa requête.

RÈGLES STRICTES:
- Pour CHAQUE suggestion, fournis les timestamps PRÉCIS (début et fin en secondes)
- Le "label" doit être court et descriptif (ex: "Savoir-faire artisan", "Citation CEO")
- L'"instruction" doit expliquer QUOI couper et POURQUOI (basé sur le contenu réel)
- Assure-toi que chaque segment a un sens complet (pas de coupure en plein milieu d'une phrase)

FORMAT JSON ATTENDU:
{
  "suggestions": [
    {
      "label": "Titre court du segment",
      "instruction": "Description de ce qui doit être coupé et pourquoi c'est intéressant",
      "startTime": 12.5,
      "endTime": 45.8,
      "duration": 33.3,
      "type": "sujet|court|format|overlay"
    }
  ]
}

IMPORTANT: Base tes suggestions sur le CONTENU RÉEL de la transcription, pas sur des hypothèses.`}
                    ]
                }],
                config: {
                    responseMimeType: "application/json",
                    responseSchema: {
                        type: Type.OBJECT,
                        properties: {
                            suggestions: {
                                type: Type.ARRAY,
                                items: {
                                    type: Type.OBJECT,
                                    properties: {
                                        label: { type: Type.STRING },
                                        instruction: { type: Type.STRING },
                                        startTime: { type: Type.NUMBER },
                                        endTime: { type: Type.NUMBER },
                                        duration: { type: Type.NUMBER },
                                        type: { type: Type.STRING, enum: ["sujet", "court", "format", "overlay"] }
                                    },
                                    required: ["label", "instruction", "startTime", "endTime", "type"]
                                }
                            }
                        },
                        required: ["suggestions"],
                    },
                }
            };

            const geminiResponse: any = await callGemini(payload);

            const responseData = geminiResponse.data as any;
            let jsonString = "";
            if (responseData.candidates && responseData.candidates[0]?.content?.parts?.[0]?.text) {
                jsonString = responseData.candidates[0].content.parts[0].text;
            } else if (responseData.text) {
                jsonString = responseData.text;
            } else if (typeof responseData === 'string') {
                jsonString = responseData;
            } else {
                throw new Error("Format de réponse IA invalide.");
            }

            const parsedSuggestions = parseAiJsonResponse<{ suggestions?: EditingSuggestion[] }>(jsonString);
            const result = parsedSuggestions.data;
            if (result.suggestions && Array.isArray(result.suggestions)) {
                setEditingSuggestions(result.suggestions);
                setMessages(prev => [...prev, { id: Date.now(), role: 'assistant', text: "J'ai analysé la vidéo ! Voici quelques suggestions de montage." }]);
            }
        } catch (error) {
            console.error("Editing suggestion generation error:", error);
        }
    }, [setMessages, setEditingSuggestions]);

    // --- Generate subtitles for a clip (full pipeline: MP3 extraction + Replicate + Background Upload) ---
    const generateSubtitlesForClip = useCallback(async (
        clip: Clip,
        mediaBlob: Blob | File,
        projectId: string,
        options?: { logFn?: (msg: string) => void, onProgress?: (p: number, s: string) => void }
    ) => {
        let loadingMessageId: string | null = null;
        let isCancelled = false;
        let globalFirestoreUnsubscribe: (() => void) | null = null;

        if (!options?.onProgress) {
            loadingMessageId = `loading-${Date.now()}`;
            setMessages(prev => [...prev, {
                id: loadingMessageId!, role: "assistant", text: "", isLoading: true, statusText: "Préparation du fichier...", progress: 0,
                onCancel: () => {
                    isCancelled = true;
                    globalFirestoreUnsubscribe?.();
                    setMessages(msgs => msgs.map(m => m.id === loadingMessageId ? {
                        ...m, isLoading: false, statusText: 'Annulée', text: `❌ Transcription de "${clip.name}" annulée.`
                    } : m));
                }
            }]);
        }

        updateClipSafe(projectId, clip.id, c => ({ ...c, isProcessingSubtitles: true }));

        const updateStatus = (statusText: string, progress: number) => {
            if (options?.onProgress) options.onProgress(progress, statusText);
            else if (loadingMessageId) setMessages(prev => prev.map(m => m.id === loadingMessageId ? { ...m, statusText, progress } : m));
        };

        const log = (msg: string) => {
            if (options?.logFn) options.logFn(msg);
            else console.log(`[EditorCore] ${msg}`);
        };

        const finalizeProgress = (statusText: string) => {
            if (options?.onProgress) options.onProgress(100, statusText);
        };

        try {
            // PIPELINE: Extract MP3 + Transcribe + Upload video ALL IN PARALLEL
            let audioUrl: string | null = null;
            let videoUrl = clip.url;
            let mp3TranscriptionSuccess = false;

            // Start video upload in BACKGROUND (runs concurrently with extraction + transcription)
            let videoUploadPromise: Promise<string | null> | null = null;
            if (!videoUrl || videoUrl.startsWith('blob:')) {
                const fileName = (mediaBlob instanceof File) ? mediaBlob.name : `video_${Date.now()}.mp4`;
                log(`🚀 Upload vidéo lancé en parallèle: ${fileName}`);
                updateClipSafe(projectId, clip.id, c => ({ ...c, isUploading: true }));

                videoUploadPromise = uploadFileToR2({
                    file: mediaBlob,
                    type: 'video',
                    logFn: log,
                    onProgress: (p) => {
                        // Only show upload progress if transcription is already done or failed
                        if (mp3TranscriptionSuccess) {
                            updateStatus(`📤 Upload vidéo... ${p}%`, 92 + Math.round(p * 0.08));
                        }
                    }
                }).then(publicUrl => {
                    log(`✅ Upload vidéo terminé: ${publicUrl}`);
                    updateClipSafe(projectId, clip.id, c => {
                        const existingSources = c.audioSources || [];
                        const withoutOriginal = existingSources.filter(s => s.type !== 'original');
                        return {
                            ...c,
                            url: publicUrl,
                            isUploading: false,
                            audioSources: [{ id: 'original', type: 'original', url: publicUrl }, ...withoutOriginal],
                            activeAudioSource: c.activeAudioSource || 'original'
                        };
                    });
                    return publicUrl;
                }).catch(err => {
                    console.error("Upload video failed", err);
                    updateClipSafe(projectId, clip.id, c => ({ ...c, isUploading: false }));
                    return null;
                });
            }

            // PRIMARY PATH: Client-side MP3 extraction + Replicate Whisper/Gemini Flash
            try {
                // STEP A: Extract MP3 client-side using FFmpeg.wasm
                updateStatus('🎧 Extraction audio MP3...', 5);
                console.log('[Transcription] Extracting MP3 client-side...');

                const mp3Blob = await FFmpegService.extractAudioFromLargeFile(
                    mediaBlob instanceof File ? mediaBlob : new File([mediaBlob], clip.name || 'video.mp4', { type: mediaBlob.type || 'video/mp4' }),
                    (ratio) => {
                        updateStatus(`🎧 Extraction audio... ${Math.round(ratio * 100)}%`, 5 + Math.round(ratio * 15));
                    }
                );
                const mp3File = new File([mp3Blob], `audio_${Date.now()}.mp3`, { type: 'audio/mpeg' });
                console.log(`[Transcription] MP3 extracted: ${(mp3File.size / 1024 / 1024).toFixed(1)} MB`);

                // STEP B: Upload MP3 to R2
                updateStatus('📤 Upload audio...', 22);
                const mp3PublicUrl = await uploadFileToR2({
                    file: mp3File,
                    type: 'audio',
                    logFn: log,
                    onProgress: (p) => {
                        updateStatus(`📤 Upload audio... ${p}%`, 22 + Math.round(p * 0.08));
                    }
                });
                console.log(`[Transcription] MP3 uploaded: ${mp3PublicUrl}`);

                // STEP C: Transcribe via Replicate Whisper + Gemini Flash
                updateStatus('🧠 Transcription Whisper + Gemini Flash...', 30);

                const transcriptionJobId = `transcription_${Date.now()}_${Math.random().toString(36).substring(7)}`;
                let raceResolved = false;

                const pollFirestoreForResult = async (): Promise<any> => {
                    const { doc, onSnapshot, getFirestore } = await import('firebase/firestore');
                    const db = getFirestore();
                    const jobRef = doc(db, 'transcription-jobs', transcriptionJobId);
                    return new Promise((resolve, reject) => {
                        globalFirestoreUnsubscribe = onSnapshot(jobRef, (snapshot) => {
                            if (isCancelled || raceResolved) { globalFirestoreUnsubscribe?.(); return; }
                            if (!snapshot.exists()) return;
                            const data = snapshot.data();
                            if (data?.status === 'complete') {
                                globalFirestoreUnsubscribe?.();
                                resolve({ data: { subtitles: data.subtitles, language: data.language } });
                            } else if (data?.status === 'error') {
                                globalFirestoreUnsubscribe?.();
                                reject(new Error(data.error || 'Transcription failed'));
                            } else if (data?.status === 'processing') {
                                const serverProgress = data.progress || 55;
                                const serverStep = data.currentStep || '⏳ Transcription en cours...';
                                updateStatus(serverStep, serverProgress);
                            }
                        });
                    });
                };

                const httpPromise = transcribeAudioConstance({
                    audioUrl: mp3PublicUrl,
                    response_format: 'verbose_json',
                    jobId: transcriptionJobId
                }).catch((err: any) => {
                    console.log('[Transcription] HTTP call dropped:', err.message);
                    return null;
                });

                const transRes = await Promise.race([
                    httpPromise.then(r => r ? r : pollFirestoreForResult()),
                    pollFirestoreForResult()
                ]);

                if (isCancelled) return;
                raceResolved = true;
                globalFirestoreUnsubscribe?.();

                const data = (transRes.data as any);
                let transcriptText = data.text || data.transcription || (data.output && data.output.text) || '';
                const segments = data.subtitles || data.segments;

                if (segments && segments.length > 0) {
                    const rawLang = data.language || 'en';
                    const sourceLang = rawLang.substring(0, 2).toLowerCase();

                    let subtitlesWithIds = segments.map((seg: any, index: number) => ({
                        id: index,
                        text: seg.text,
                        start: seg.start,
                        end: seg.end
                    }));
                    subtitlesWithIds = fixSubtitleOverlaps(subtitlesWithIds);

                    updateClipSafe(projectId, clip.id, c => ({
                        ...c,
                        subtitles: { [sourceLang]: subtitlesWithIds }
                    }));

                    setActiveSubtitleLanguage(sourceLang);
                    if (loadingMessageId) setMessages(prev => prev.filter(m => m.id !== loadingMessageId));

                    mp3TranscriptionSuccess = true;
                    finalizeProgress('✅ Transcription terminée');
                }
            } catch (mp3Error) {
                console.error('[Transcription] MP3 extraction or Replicate failed, falling back:', mp3Error);
            }

            if (mp3TranscriptionSuccess) return;

            if (videoUploadPromise) {
                const uploadedUrl = await videoUploadPromise;
                if (uploadedUrl) videoUrl = uploadedUrl;
            }

            const transcribeUrl = audioUrl || videoUrl;
            updateStatus("Extraction et transcription serveur...", 45);

            const transcriptionJobId = `transcription_app_${Date.now()}_${Math.random().toString(36).substring(7)}`;

            const pollFirestoreForTranscription = async (): Promise<any> => {
                const { doc, onSnapshot, getFirestore } = await import('firebase/firestore');
                const db = getFirestore();
                const jobRef = doc(db, 'transcription-jobs', transcriptionJobId);
                return new Promise((resolve, reject) => {
                    globalFirestoreUnsubscribe = onSnapshot(jobRef, (snapshot) => {
                        if (isCancelled) { globalFirestoreUnsubscribe?.(); return; }
                        if (!snapshot.exists()) return;
                        const data = snapshot.data();
                        if (data?.status === 'complete') {
                            globalFirestoreUnsubscribe?.();
                            resolve({ data: { subtitles: data.subtitles, language: data.language } });
                        } else if (data?.status === 'error') {
                            globalFirestoreUnsubscribe?.();
                            reject(new Error(data.error || 'Transcription failed'));
                        } else if (data?.status === 'processing') {
                            updateStatus('⏳ Transcription en cours (traitement long)...', 50);
                        }
                    });
                });
            };

            const httpPromise = transcribeAudioConstance({
                audioUrl: transcribeUrl,
                isLargeFile: true,
                useGpt4oTranscribe: false,
                jobId: transcriptionJobId
            }).catch(() => null);

            const transcriptionResult = await Promise.race([
                httpPromise.then(r => r ? r : pollFirestoreForTranscription()),
                pollFirestoreForTranscription()
            ]);

            const { subtitles: sourceSubtitles, language } = (transcriptionResult.data as any);
            updateStatus(`Formatage des sous-titres...`, 90);

            let sourceLang = (language || 'en').substring(0, 2).toLowerCase();
            if (!['fr', 'en', 'es', 'de', 'it', 'pt', 'nl'].includes(sourceLang)) sourceLang = 'fr';

            let subtitlesWithTimelineOffset = sourceSubtitles.map((sub: Subtitle, index: number) => ({
                ...sub, id: index, start: sub.start, end: sub.end
            }));
            subtitlesWithTimelineOffset = fixSubtitleOverlaps(subtitlesWithTimelineOffset);

            updateClipSafe(projectId, clip.id, c => ({ ...c, subtitles: { [sourceLang]: subtitlesWithTimelineOffset } }));
            setActiveSubtitleLanguage(sourceLang);
            if (loadingMessageId) setMessages(prev => prev.filter(m => m.id !== loadingMessageId));

            finalizeProgress('✅ Transcription terminée');
        } catch (error) {
            console.error("Erreur lors de la génération des sous-titres :", error);
            finalizeProgress('❌ Erreur de transcription');
            if (loadingMessageId) {
                setMessages(prev => [...prev.filter(m => m.id !== loadingMessageId), {
                    id: Date.now(), role: 'assistant', text: `Erreur : ${error instanceof Error ? error.message : 'Inconnue'}`
                }]);
            }
        } finally {
            updateClipSafe(projectId, clip.id, c => ({ ...c, isProcessingSubtitles: false }));
        }
    }, [updateClipSafe, uploadFileToR2, setMessages, setActiveSubtitleLanguage]);

    // --- Generate Subtitles (Standard path, ported from App.tsx) ---
    const handleGenerateSubtitles = useCallback(async (clip: Clip) => {
        if (!clip.url || clip.isProcessingSubtitles) return;

        // Clear existing subtitles immediately before starting the new request
        updateClipSafe(currentProjectId!, clip.id, c => ({
            ...c,
            isProcessingSubtitles: true,
            subtitles: undefined,       // Remove existing subtitles object
            subtitlesUrl: undefined,    // Remove associated URL if any
            _compactSubtitles: undefined // Remove cached data
        }));

        let firestoreUnsubscribe: (() => void) | null = null;
        let isCancelled = false;
        const loadingMessageId = Date.now();
        setMessages(prev => [...prev, {
            id: loadingMessageId,
            role: 'assistant',
            text: `⏳ Transcription de "${clip.name}" en cours...`,
            isLoading: true,
            onCancel: () => {
                isCancelled = true;
                firestoreUnsubscribe?.();
                setMessages(msgs => msgs.map(m => m.id === loadingMessageId ? {
                    ...m,
                    isLoading: false,
                    statusText: 'Annulée',
                    text: `❌ Transcription de "${clip.name}" annulée par l'utilisateur.`
                } : m));
            }
        }]);

        try {
            if (isCancelled) return;
            const transcriptionJobId = `transcription_${Date.now()}_${Math.random().toString(36).substring(7)}`;

            const pollFirestoreForResult = async (): Promise<any> => {
                const { doc, onSnapshot, getFirestore } = await import('firebase/firestore');
                const db = getFirestore();
                const jobRef = doc(db, 'transcription-jobs', transcriptionJobId);
                return new Promise((resolve, reject) => {
                    firestoreUnsubscribe = onSnapshot(jobRef, (snapshot) => {
                        if (isCancelled) {
                            firestoreUnsubscribe?.();
                            resolve(null);
                            return;
                        }
                        if (!snapshot.exists()) return;
                        const data = snapshot.data();
                        if (data?.status === 'complete') {
                            firestoreUnsubscribe?.();
                            resolve({ data: { subtitles: data.subtitles, language: data.language } });
                        } else if (data?.status === 'error') {
                            firestoreUnsubscribe?.();
                            reject(new Error(data.error || 'Transcription failed'));
                        } else if (data?.status === 'processing') {
                            const serverProgress = data.progress || 50;
                            const serverStep = data.currentStep || '⏳ Transcription en cours...';
                            setMessages(prev => prev.map(m => m.id === loadingMessageId ? {
                                ...m,
                                statusText: `${serverStep} (${serverProgress}%)`,
                                progress: serverProgress
                            } : m));
                        }
                    });
                });
            };

            // Essayer de trouver l'extrait MP3 s'il existe
            let audioUrl = clip.url;
            if (audioUrl && !audioUrl.startsWith('blob:')) {
                const potentialMp3Url = audioUrl.replace(/\.(mp4|mov|webm)$/i, '.mp3');
                try {
                    // Faire une requête HEAD pour voir si le fichier existe vraiment (status 200)
                    const headResponse = await fetch(potentialMp3Url, { method: 'HEAD' });
                    if (headResponse.ok) {
                        audioUrl = potentialMp3Url;
                        console.log('[GenerateSubtitles] Fichier MP3 trouvé avec succès:', audioUrl);
                    } else {
                        console.log('[GenerateSubtitles] MP3 introuvable, demande d\'extraction au serveur...');

                        // Appeler le backend d'export pour extraire le fichier
                        const EXPORT_SERVICE_URL = import.meta.env.VITE_EXPORT_SERVICE_URL || 'https://export-service-400620783493.us-central1.run.app';

                        const extractResponse = await fetch(`${EXPORT_SERVICE_URL}/extract-audio`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                videoUrl: audioUrl,
                                jobId: `extract_${Date.now()}`
                            })
                        });

                        if (!extractResponse.ok) {
                            throw new Error(`Erreur serveur pendant l'extraction: ${extractResponse.statusText}`);
                        }

                        const extractData = await extractResponse.json();
                        audioUrl = extractData.audioUrl;
                        console.log('[GenerateSubtitles] Serveur a extrait le MP3 avec succès:', audioUrl);

                        // Mettre à jour le statut
                        setAllClips(prev => prev.map(c =>
                            c.id === clip.id ? { ...c, isProcessingSubtitles: true } : c
                        ));
                    }
                } catch (e) {
                    console.log('[GenerateSubtitles] Erreur réseau lors de la vérification/extraction du MP3, utilisation de la source vidéo en fallback.', e);
                }
            }

            const httpPromise = transcribeAudioConstance({
                audioUrl: audioUrl,
                isLargeFile: true,
                useGpt4oTranscribe: false,
                jobId: transcriptionJobId,
                forceRegenerate: true
            }).catch((err: any) => {
                console.log('[GenerateSubtitles] HTTP call dropped:', err.message);
                return null;
            });

            const transRes = await Promise.race([
                httpPromise.then((r: any) => r ? r : pollFirestoreForResult()),
                pollFirestoreForResult()
            ]);

            if (isCancelled) return;

            const data = transRes.data as any;
            const segments = data.subtitles || data.segments;

            if (segments && segments.length > 0) {
                const rawLang = data.language || 'en';
                const sourceLang = rawLang.substring(0, 2).toLowerCase();

                let subtitlesWithIds = segments.map((seg: any, index: number) => ({
                    id: index,
                    text: seg.text,
                    start: seg.start,
                    end: seg.end
                }));
                subtitlesWithIds = fixSubtitleOverlaps(subtitlesWithIds);

                updateClipSafe(currentProjectId!, clip.id, c => ({
                    ...c,
                    subtitles: { [sourceLang]: subtitlesWithIds }
                }));
                setActiveSubtitleLanguage(sourceLang);

                setMessages(prev => prev.map(m => m.id === loadingMessageId ? {
                    ...m,
                    isLoading: false,
                    statusText: 'Terminé',
                    progress: 100,
                    text: `✅ Transcription terminée pour "${clip.name}" (${subtitlesWithIds.length} sous-titres générés)`
                } : m));
            } else {
                throw new Error("Aucun sous-titre généré");
            }
        } catch (err: any) {
            if (isCancelled) return;
            firestoreUnsubscribe?.();
            setMessages(prev => prev.map(m => m.id === loadingMessageId ? {
                ...m,
                isLoading: false,
                statusText: 'Erreur',
                text: `❌ Erreur de transcription: ${err.message}`
            } : m));
        } finally {
            updateClipSafe(currentProjectId!, clip.id, c => ({ ...c, isProcessingSubtitles: false }));
        }
    }, [currentProjectId, updateClipSafe, setMessages, setActiveSubtitleLanguage]);

    // --- Handle file drop on timeline ---
    const handleTimelineFileDrop = useCallback(async (
        file: File | Blob | null,
        clip: Clip,
        projectId?: string,
        options?: { logFn?: (msg: string) => void, onProgress?: (p: number, s: string) => void }
    ) => {
        if (!file) return;
        const targetProjectId = projectId || currentProjectId;
        if (!targetProjectId) return;

        try {
            const isFile = file instanceof File;
            const fileName = isFile ? file.name : `${clip.name || 'media'}.mp4`;

            // Use robust upload
            const r2Url = await uploadFileToR2({
                file: file,
                type: 'video', // Assumed video for timeline drops usually, or we can detect
                logFn: options?.logFn,
                onProgress: options?.onProgress ? (p) => options.onProgress!(p, 'Upload...') : undefined
            });

            if (r2Url) {
                updateClipSafe(targetProjectId, clip.id, c => {
                    const localUrl = getClipLocalSourceUrl(c);
                    return {
                        ...c,
                        url: r2Url,
                        ...(localUrl ? { localUrl } : {}),
                    };
                });

                // Auto-generate subtitles
                if (clip.type === 'video') {
                    // Pass options if generateSubtitlesForClip is updated to accept them
                    generateSubtitlesForClip(clip, file, targetProjectId);
                }
            }
        } catch (error) {
            console.error('[TimelineFileDrop] Failed:', error);
            if (options?.logFn) options.logFn(`❌ Upload failed: ${error}`);
        }
    }, [currentProjectId, updateClipSafe, generateSubtitlesForClip]);

    // --- Translate subtitles (full implementation) ---
    const handleTranslateSubtitles = useCallback(async (targetLang: string, silent: boolean = false, explicitClip?: Clip) => {
        if (translationsInProgress.has(targetLang)) {
            alert(`Une traduction vers ${targetLang.toUpperCase()} est déjà en cours. Veuillez patienter.`);
            return;
        }

        const hasValidSubtitles = (clip?: Clip) =>
            !!clip?.subtitles &&
            Object.keys(clip.subtitles).length > 0 &&
            Object.values(clip.subtitles).some((arr: any) => arr && arr.length > 0);

        const findClipWithSubtitles = (clips: Clip[]) => {
            if (explicitClip) {
                if (hasValidSubtitles(explicitClip)) return explicitClip;
                const explicitMatch = clips.find(c => c.id === explicitClip.id && hasValidSubtitles(c));
                if (explicitMatch) return explicitMatch;
            }

            if (selectedClipId) {
                const selectedMatch = clips.find(c => c.id === selectedClipId && hasValidSubtitles(c));
                if (selectedMatch) return selectedMatch;
            }

            return clips.find(hasValidSubtitles);
        };

        let subtitleSearchClips = allClips;
        let clipWithSubtitles = findClipWithSubtitles(subtitleSearchClips);

        if (!clipWithSubtitles && currentProjectId) {
            const projectSnapshot = savedProjects.find(project => project.id === currentProjectId);
            if (projectSnapshot) {
                try {
                    const hydratedProject = await hydrateProjectSubtitles(projectSnapshot);
                    subtitleSearchClips = collectProjectClips(hydratedProject);
                    clipWithSubtitles = findClipWithSubtitles(subtitleSearchClips);
                } catch (error) {
                    console.warn('[Translate] Failed to hydrate subtitles before translation:', error);
                }
            }
        }

        if (!clipWithSubtitles || !clipWithSubtitles.subtitles) {
            alert('Aucun sous-titre disponible à traduire. Veuillez d\'abord générer la transcription.');
            return;
        }

        const availableLanguages = Object.keys(clipWithSubtitles.subtitles);
        if (availableLanguages.length === 0) {
            alert('Aucun sous-titre disponible à traduire.');
            return;
        }

        if (clipWithSubtitles.subtitles[targetLang] && clipWithSubtitles.subtitles[targetLang].length > 0) {
            if (!silent) {
                const confirmReTranslate = window.confirm(`Une traduction en ${targetLang.toUpperCase()} existe déjà. Voulez-vous la retraduire ?`);
                if (!confirmReTranslate) return;
            }
        }

        setTranslationsInProgress(prev => new Set(prev).add(targetLang));

        let isCancelled = false;
        let globalFirestoreUnsubscribe: (() => void) | null = null;
        const sourceLang = availableLanguages[0];
        const sourceSubtitles = clipWithSubtitles.subtitles[sourceLang];
        const translationMessageId = `translation-${targetLang}-${Date.now()}`;

        setMessages(prev => [...prev, {
            id: translationMessageId,
            role: 'assistant',
            text: '',
            isLoading: true,
            statusText: `Traduction vers ${targetLang.toUpperCase()} en cours...`,
            progress: 0,
            onCancel: () => {
                isCancelled = true;
                globalFirestoreUnsubscribe?.();
                setMessages(msgs => msgs.map(m => m.id === translationMessageId ? {
                    ...m, isLoading: false, statusText: 'Annulée', text: `❌ Traduction vers ${targetLang.toUpperCase()} annulée.`
                } : m));
                setTranslationsInProgress(curr => {
                    const next = new Set(curr);
                    next.delete(targetLang);
                    return next;
                });
            }
        }]);

        try {
            setMessages(prev => prev.map(m => m.id === translationMessageId ? {
                ...m, statusText: `Traduction vers ${targetLang.toUpperCase()} en cours (traitement complet)...`, progress: 10
            } : m));

            const translationJobId = `translation_${targetLang}_${Date.now()}_${Math.random().toString(36).substring(7)}`;

            const pollFirestoreForTranslation = async (): Promise<any> => {
                const { doc, onSnapshot, getFirestore } = await import('firebase/firestore');
                const db = getFirestore();
                const jobRef = doc(db, 'translation-jobs', translationJobId);

                return new Promise((resolve, reject) => {
                    globalFirestoreUnsubscribe = onSnapshot(jobRef, (snapshot) => {
                        if (isCancelled) { globalFirestoreUnsubscribe?.(); return; }
                        if (!snapshot.exists()) return;
                        const data = snapshot.data();

                        if (data?.status === 'complete') {
                            globalFirestoreUnsubscribe?.();
                            resolve({ data: { subtitles: data.subtitles, language: targetLang } });
                        } else if (data?.status === 'error') {
                            globalFirestoreUnsubscribe?.();
                            reject(new Error(data.error || 'Translation failed'));
                        } else if (data?.status === 'processing') {
                            if (data.progress) {
                                setMessages(prev => prev.map(m => m.id === translationMessageId ? { ...m, progress: data.progress } : m));
                            }
                        }
                    });
                });
            };

            const httpPromise = translateSubtitlesBatch({
                subtitles: sourceSubtitles,
                sourceLang,
                targetLang,
                batchStart: 0,
                jobId: translationJobId
            }).catch(() => null);

            const result = await Promise.race([
                httpPromise.then(r => r ? r : pollFirestoreForTranslation()),
                pollFirestoreForTranslation()
            ]);

            if (isCancelled) return;

            const resultData = result.data as { subtitles: Subtitle[] };
            const translatedSubtitles = resultData.subtitles || [];

            if (translatedSubtitles.length === 0) throw new Error("La traduction n'a renvoyé aucun résultat.");

            setMessages(prev => prev.map(m => m.id === translationMessageId ? {
                ...m, statusText: `Application des traductions...`, progress: 90
            } : m));

            let translatedWithIds = translatedSubtitles.map((sub: Subtitle, index: number) => ({
                ...sub, id: index,
            }));
            translatedWithIds = fixSubtitleOverlaps(translatedWithIds) as typeof translatedWithIds;

            updateClips(prev => {
                const next = prev.map(c => {
                    const isSelectedClip = c.id === clipWithSubtitles.id;
                    const isSameVideo = c.url && clipWithSubtitles.url && c.url === clipWithSubtitles.url;

                    if (isSelectedClip || (isSameVideo && c.type === 'video')) {
                        return { ...c, subtitles: { ...(c.subtitles || {}), [targetLang]: translatedWithIds } };
                    }
                    return c;
                });
                return next;
            }, false);

            setMessages(prev => prev.map(m => m.id === translationMessageId ? {
                ...m, isLoading: false, text: `✅ Traduction vers ${targetLang.toUpperCase()} terminée.`, progress: 100
            } : m));

            setActiveSubtitleLanguage(targetLang);

            // Propagate translated subtitles to ALL other montage versions
            setMontageVersions(prev => prev.map(version => {
                if (version.id === currentVersionId) return version; // Already synced via useEffect
                const updatedClips = version.clips.map(vClip => {
                    if (!vClip.url || vClip.url !== clipWithSubtitles.url) return vClip;
                    return { ...vClip, subtitles: { ...(vClip.subtitles || {}), [targetLang]: translatedWithIds } };
                });
                return { ...version, clips: updatedClips };
            }));

        } catch (error) {
            console.error("La traduction a échoué:", error);
            setMessages(prev => prev.map(m => m.id === translationMessageId ? {
                ...m, isLoading: false, text: `⚠️ La traduction vers ${targetLang.toUpperCase()} a échoué. Veuillez réessayer.`,
            } : m));
        } finally {
            setTranslationsInProgress(prev => {
                const next = new Set(prev);
                next.delete(targetLang);
                return next;
            });
        }
    }, [allClips, collectProjectClips, currentProjectId, currentVersionId, hydrateProjectSubtitles, savedProjects, selectedClipId, setActiveSubtitleLanguage, setMessages, setMontageVersions, setTranslationsInProgress, translationsInProgress, updateClips]);

    // --- Handle initial files drop ---
    const handleInitialFilesDrop = useCallback(async (files: File[]) => {
        let cumulativeTime = 0;
        const newClips: Clip[] = [];

        if (allClips.length > 0) {
            cumulativeTime = Math.max(0, ...allClips.map(c => c.start + c.duration));
        }

        const sortedFiles = [...files].sort((a, b) => {
            if (a.type.startsWith('video/')) return -1;
            if (b.type.startsWith('video/')) return 1;
            return 0;
        });

        for (const file of sortedFiles) {
            const alreadyExists = allClips.some(c => c.name === file.name);
            if (alreadyExists) continue;

            const isVideo = file.type.startsWith('video/');
            const isGif = file.type === 'image/gif';
            const type: Clip['type'] = isGif || isVideo ? 'video' : file.type.startsWith('audio/') ? 'audio' : 'image';

            const fileUrl = URL.createObjectURL(file);
            const duration = await getMediaDurationFromUrl(fileUrl);

            const preferredTrackId = isVideo ? 'v2' : 'v1';
            let targetTrack = tracks.find(t => t.id === preferredTrackId);
            if (!targetTrack || !targetTrack.accepts.includes(type)) {
                targetTrack = tracks.find(t => t.accepts.includes(type) && !['t1', 's1', 'z1'].includes(t.id));
            }
            if (!targetTrack) continue;

            const newClip = createNewClip({
                name: file.name,
                url: fileUrl,
                localUrl: fileUrl,
                type,
                trackId: targetTrack.id,
                start: cumulativeTime,
                duration,
                originalDuration: duration,
                waveform: [],
                thumbnails: [],
            });
            (newClip as any)._file = file;
            newClips.push(newClip);
            cumulativeTime += duration;
        }

        if (newClips.length > 0) {
            updateClips(prev => [...prev, ...newClips]);

            // --- Determine Target Project ID UPFRONT ---
            const firstClip = newClips[0];
            const projectName = firstClip.name.split('.').slice(0, -1).join('.') || "Nouveau projet";

            // Logic to reuse or create new project
            const targetProjectId = currentProjectId && allClips.length === 0
                ? currentProjectId
                : `proj_${Date.now()}`;

            // Upload log setup
            const uploadMsgId = `upload-batch-${targetProjectId}`;
            const videoClips = newClips.filter(c => c.type === 'video' && !c.name.toLowerCase().endsWith('.gif'));

            // Helper for chat logging
            const logToChat = (msg: string) => {
                const time = new Date().toLocaleTimeString('en-US', { hour12: true });
                setMessages(prev => prev.map(m => {
                    if (m.id === uploadMsgId) {
                        return {
                            ...m,
                            text: `${m.text}\n[${time}] ${msg}`
                        };
                    }
                    return m;
                }));
            };

            if (videoClips.length > 0) {
                const fileNames = videoClips.map(c => c.name).join(', ');
                const startTime = new Date().toLocaleTimeString('en-US', { hour12: true });
                setMessages(prev => [...prev, {
                    id: uploadMsgId,
                    role: 'assistant',
                    text: `[${startTime}] 📥 ${videoClips.length > 1 ? 'Vidéos reçues' : 'Vidéo reçue'}: **${fileNames}**\n[${startTime}] ☁️ Initialisation...`,
                    isLoading: true,
                    progress: 0,
                    statusText: "Préparation..."
                }]);
            }

            // Track overall progress
            const progressMap = new Map<string, number>();

            // Process Clips - Upload Videos
            newClips.forEach(async (clip) => {
                const isGif = clip.name.toLowerCase().endsWith('.gif');

                if (clip.type === 'video' && !isGif && clip.url) {
                    generateThumbnails(clip.url).then(thumbnails => {
                        updateClipSafe(targetProjectId, clip.id, c => ({ ...c, thumbnails }));
                    });

                    const blobOrFile = (clip as any)._file;
                    if (blobOrFile) {
                        try {
                            const publicUrl = await uploadFileToR2({
                                file: blobOrFile,
                                type: 'video',
                                logFn: logToChat,
                                onProgress: (p) => {
                                    progressMap.set(clip.id, p);
                                    let total = 0;
                                    progressMap.forEach(v => total += v);
                                    const avg = Math.round(total / videoClips.length);

                                    setMessages(prev => prev.map(m => m.id === uploadMsgId ? {
                                        ...m,
                                        statusText: `Upload global: ${avg}%`,
                                        progress: avg
                                        // No text overwrite
                                    } : m));
                                }
                            });

                            updateClipSafe(targetProjectId, clip.id, c => {
                                const localUrl = getClipLocalSourceUrl(c);
                                return {
                                    ...c,
                                    url: publicUrl,
                                    ...(localUrl ? { localUrl } : {}),
                                };
                            });

                            progressMap.set(clip.id, 100);

                            // Check all done
                            let allDone = true;
                            if (progressMap.size === videoClips.length) {
                                progressMap.forEach(v => { if (v < 100) allDone = false; });
                            } else {
                                allDone = false;
                            }

                            if (allDone) {
                                const time = new Date().toLocaleTimeString('en-US', { hour12: true });
                                setMessages(prev => prev.map(m => m.id === uploadMsgId ? {
                                    ...m,
                                    isLoading: false,
                                    statusText: "Terminé",
                                    text: `${m.text}\n[${time}] ✅ Tous les fichiers sont uploadés !`
                                } : m));
                            }
                        } catch (e) {
                            console.error("Upload failed for " + clip.name, e);
                            logToChat(`❌ Erreur upload pour ${clip.name}`);
                        }
                    }
                }

                if (clip.type === 'audio' && clip.url) {
                    generateWaveformData(clip.url).then(waveform => {
                        updateClipSafe(targetProjectId, clip.id, c => ({ ...c, waveform }));
                    });
                }
            });

            // Create or update project
            if (currentProjectId && allClips.length === 0) {
                // Reuse current empty project
                setSavedProjects(prev => prev.map(p => {
                    if (p.id === currentProjectId) {
                        const updatedProject = {
                            ...p,
                            name: projectName,
                            clips: [...newClips],
                            updatedAt: Date.now()
                        };
                        saveProjectToFirestore(updatedProject).catch(console.error);
                        return updatedProject;
                    }
                    return p;
                }));
            } else if (!currentProjectId) {
                // Create new project
                let projectThumbnail = '';
                const firstVisual = newClips.find(c => c.type === 'video' || c.type === 'image');
                if (firstVisual?.url) {
                    try {
                        projectThumbnail = firstVisual.type === 'image' ? firstVisual.url : await generateThumbnailFromUrl(firstVisual.url);
                    } catch (e) { /* ignore */ }
                }

                const newProject: SavedProject = {
                    id: targetProjectId,
                    name: projectName,
                    thumbnailUrl: projectThumbnail,
                    clips: [...newClips],
                    tracks: tracks,
                    createdAt: Date.now(),
                    updatedAt: Date.now()
                };

                setSavedProjects(prev => [newProject, ...prev]);
                setRootProjects(prev => [{
                    id: newProject.id,
                    title: newProject.name,
                    updated: new Date().toLocaleDateString(),
                    thumbnailUrl: newProject.thumbnailUrl,
                    assets: []
                }, ...prev]);
                saveProjectToFirestore(newProject).catch(console.error);
                setCurrentProjectId(newProject.id);
            }
        }
    }, [tracks, allClips, updateClips, currentProjectId, handleTimelineFileDrop, updateClipSafe]);

    // --- Handle select sidebar project ---
    const handleSelectSidebarProject = useCallback((p: SidebarProject) => {
        const existsInSaved = savedProjects.some(sp => sp.id === p.id);

        if (!existsInSaved) {
            const newSavedProject: SavedProject = {
                id: p.id,
                name: p.title || "Nouveau projet",
                clips: [],
                tracks: INITIAL_TRACKS.map(t => ({ ...t })),
                createdAt: Date.now(),
                updatedAt: Date.now(),
                thumbnailUrl: p.thumbnailUrl,
            };
            setSavedProjects(prev => [newSavedProject, ...prev]);
            // Persist immediately to avoid route -> not found on reload.
            saveProjectToFirestore(newSavedProject).catch(err => {
                console.error('[ProjectNav] Failed to persist new sidebar project:', err);
            });
        }

        navigate(`${basePath}/project/${p.id}`);
    }, [savedProjects, navigate, basePath, INITIAL_TRACKS]);

    // --- Save master ---
    const handleSaveMaster = useCallback(async (data: { name: string }) => {
        if (!user) return;
        try {
            const template: MasterTemplate = {
                id: currentScope.id?.includes('template') ? currentScope.id : `template_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                name: data.name,
                type: currentScope.type,
                syntheData: {
                    clips: currentScope.syntheData?.clips || [],
                    tracks: currentScope.syntheData?.tracks || [],
                    backgroundColor: currentScope.syntheData?.backgroundColor
                },
                ownerId: user.uid,
                createdAt: Date.now()
            };
            await saveMasterTemplate(template);
            setMessages(prev => [...prev, {
                id: Date.now(),
                role: 'assistant',
                text: `✅ Modèle Master "${data.name}" enregistré avec succès dans le cloud.`
            }]);
        } catch (error) {
            console.error("Save master error:", error);
            setMessages(prev => [...prev, {
                id: Date.now(),
                role: 'assistant',
                text: `❌ Erreur lors de la sauvegarde : ${error instanceof Error ? error.message : "Erreur inconnue"}`
            }]);
        }
    }, [user, currentScope, setMessages]);

    // --- Library ---
    const handleAddLibraryItem = useCallback(async (item: any, category: any, mode?: string) => {
        const currentTime = timelinePlayerRef.current?.getCurrentTime() || 0;

        if (category.id === 'synthes') {
            const defaultClipsWithUniqueIds = DEFAULT_SYNTHE_CLIPS.map((clip, idx) => ({
                ...clip,
                id: `synthe-text-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`
            }));
            const newClip = createNewClip({
                type: 'synthe',
                name: item.name,
                start: currentTime,
                duration: 5,
                trackId: 't1',
                syntheData: item.syntheData || { clips: defaultClipsWithUniqueIds, tracks: INITIAL_TRACKS }
            });
            updateClips(prev => [...prev, newClip]);
            return;
        }

        if (category.id === 'panels') {
            const defaultPanelClipsWithUniqueIds = DEFAULT_PANEL_CLIPS.map((clip, idx) => ({
                ...clip,
                id: `panel-text-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`
            }));
            const newClip = createNewClip({
                type: 'panel',
                name: item.name,
                start: currentTime,
                duration: 5,
                trackId: 'v1',
                syntheData: item.syntheData || { clips: defaultPanelClipsWithUniqueIds, tracks: INITIAL_TRACKS, backgroundColor: '#000000' }
            });
            updateClips(prev => [...prev, newClip]);
            return;
        }
    }, [updateClips, INITIAL_TRACKS]);

    // --- Chat ---
    const handleSendMessage = useCallback(async (text: string, elements: MontageElements) => {
        addUserMessage(text);
        setCurrentMontageElements(elements);
        // The actual AI processing would go here — for now delegate to the same
        // generateAndApplyEditPlan pattern. This is a simplified version.
        setMessages(prev => [...prev, {
            id: Date.now(),
            role: 'assistant',
            text: "Cette fonctionnalité sera complètement portée dans une prochaine mise à jour."
        }]);
    }, [addUserMessage, setMessages]);

    const handleEditChoice = useCallback(async (choice: 'current' | 'original' | 'yes' | 'no', pendingChoice: ChatPendingChoice) => {
        setMessages(prev => prev.filter(m => {
            if (!m.pendingChoice) return true;
            if (m.pendingChoice.type !== pendingChoice.type) return true;

            if (pendingChoice.type === 'edit-base-choice') {
                return m.pendingChoice.originalRequest !== pendingChoice.originalRequest;
            }

            return m.pendingChoice.projectId !== pendingChoice.projectId;
        }));

        if (pendingChoice.type === 'local-cache-choice') {
            if (choice === 'yes') {
                await hydrateProjectLocalCache(pendingChoice.projectId);
            } else {
                setMessages(prev => [...prev, {
                    id: Date.now(),
                    role: 'assistant',
                    text: "OK, on garde les sources distantes pour cette session."
                }]);
            }
            return;
        }

        // Placeholder - edit-base-choice will be wired to the full editing flow later.
    }, [hydrateProjectLocalCache, setMessages]);

    // --- Track updates for nested synthé ---
    const handleSetTracks = useCallback((newTracks: Track[] | ((prev: Track[]) => Track[])) => {
        if (navigationStack.length === 0) {
            setTracks(newTracks);
        } else {
            setAllClips((prev: Clip[]) => {
                const stackIds = navigationStack.map(c => c.id);
                const updateRecursive = (currentClips: Clip[], depth: number): Clip[] => {
                    if (depth >= stackIds.length) return currentClips;
                    const targetId = stackIds[depth];
                    return currentClips.map(clip => {
                        if (clip.id === targetId) {
                            if (depth === stackIds.length - 1) {
                                return {
                                    ...clip,
                                    syntheData: { ...clip.syntheData!, tracks: typeof newTracks === 'function' ? newTracks(clip.syntheData!.tracks) : newTracks }
                                };
                            }
                            return {
                                ...clip,
                                syntheData: { ...clip.syntheData!, clips: updateRecursive(clip.syntheData?.clips || [], depth + 1) }
                            };
                        }
                        return clip;
                    });
                };
                return updateRecursive(prev, 0);
            });
        }
    }, [navigationStack, setTracks, setAllClips]);

    // ============================================================================
    // RETURN — Everything the Pro UI needs
    // ============================================================================

    return {
        // Auth
        user,

        // Routing
        navigate,
        basePath,

        // UI state
        activeTab, setActiveTab,
        isInitialDropzoneVisible,
        projectLoadingState,
        scaleFactor, setScaleFactor,
        selectedSubtitleId, setSelectedSubtitleId,

        // Refs
        timelinePlayerRef,

        // Clip management
        allClips, setAllClips,
        tracks, setTracks: handleSetTracks,
        selectedClipId, setSelectedClipId,
        selectedClip,
        isClipContextMenuOpen, setIsClipContextMenuOpen,
        handleClipSelect: onClipSelect,
        history, historyIndex, undo, redo,
        navigationStack, setNavigationStack,
        currentScope,
        updateClips,
        INITIAL_TRACKS,

        // Chat
        messages, setMessages,
        editingSuggestions, setEditingSuggestions,
        handleSendMessage,
        handleEditChoice,

        // Project
        currentProjectId, setCurrentProjectId,
        savedProjects, setSavedProjects,
        folders, setFolders,
        rootProjects, setRootProjects,
        sidebarOpen, setSidebarOpen,
        loadProject,

        // Export
        isExporting, setIsExporting,
        exportStatus, setExportStatus,
        exportProgress, setExportProgress,
        exportDetails, setExportDetails,
        exportedVideos, setExportedVideos,
        exportAspectRatio, setExportAspectRatio,
        scaleMode, setScaleMode,
        vfxSegmentPreviews,
        serverExportStatus,
        isTimelineEligibleForFastExport,
        handleStartExport,
        handleCancelExport,
        handleStartServerExport,
        handleCancelServerExport,
        handleDeleteExport,
        uploadAssetToStorage,
        exportStatusByProject,
        chunks,
        isMerging,
        retryChunkDownload,
        handleStartHybridExport,
        hasTimelineOverlays,
        totalTimelineDuration,

        // Subtitles
        subtitleStyle, setSubtitleStyle,
        activeSubtitleLanguage, setActiveSubtitleLanguage,
        handleTranslateSubtitles,
        translationsInProgress,

        // Montage versions
        montageVersions, setMontageVersions,
        currentVersionId, setCurrentVersionId,
        handleSelectVersion,
        addMontageVersion,
        ensureBaseVersion,

        // Voiceover
        generatedVoices, setGeneratedVoices,
        voiceoverText, setVoiceoverText,

        // Handlers
        handleClipVolumeChange,
        handleSelectAudioSource,
        handleManualSave,
        handleInitialFilesDrop,
        handleTimelineFileDrop,
        handleOpenExportMenu,
        handleSeekRequest,
        handleSubtitleClick,
        handleCloseClipContextMenu,
        handleAddLibraryItem,
        handleSelectSidebarProject,
        handleDeleteProject,
        handleDuplicateProject,
        handleRenameProject,
        handleShareProject,
        shareToast, setShareToast,
        handleSaveMaster,
        navigateIntoClip,
        navigateUp,

        // Transcription
        useGpt4oTranscribe, setUseGpt4oTranscribe,
        handleGenerateSubtitles,
    };
}
